// selectLotPreBids — the single statement of "which stored maximums are this
// lot's pre-bids, and in what order" (BL-1325).
//
// WHY THIS IS A NAMED FUNCTION AND NOT AN INLINE FILTER. Four places in this
// repo answer some form of "is this `max_bids` row a pre-bid": the pre-bid
// counts query (`max-bids-repository.ts`, correct), the admin pre-bid publisher
// and its catch-up factory (deliberate pass-throughs — they carry every row and
// project the flag), and the clerk console. The console's answer was MISSING
// entirely, which is the defect this file closes. Writing the predicate inline
// in the hook would have taken the concept from two stated paths to three
// (ADR 044 § 5, paths-per-concept) — so it is stated once, here, and the older
// Timed Controls feed has something to migrate onto when its own boundary
// heuristic is retired.
//
// MEMBERSHIP IS THE FLAG, NOT A TIMESTAMP. A row belongs to the pre-bid section
// iff `placedDuringPreBid` is true — the value the write path stamped at
// placement and never revisits (`max_bids.placed_during_pre_bid`, whose own
// column comment reads "Not cleared when the auction opens — keeps the audit
// trail"). The transport carries EVERY `max_bids` row for the auction on
// purpose, so without this predicate an ordinary live proxy maximum renders
// under a heading that says "Pre-bids" — and, because the edit/delete API gates
// on lot phase rather than on pre-bid-ness, the actions offered on it work.
//
// A BIDDER WHO RAISES AFTER THE OPEN keeps both rows: the original pre-bid row
// holds its flag forever and merely flips to `superseded`, while the live raise
// is a NEW row flagged false. So the section keeps the full pre-bid story and
// correctly sheds the live ceiling.
//
// Pure and generic on input shape per ADR 010 — no I/O, no React, safe inside a
// `useMemo` every render, and unit-testable without a renderer.

/** The minimum a row must carry to be placed (or not) in a lot's pre-bid list. */
export interface LotPreBidCandidate {
    /** `max_bids.lot_id` — the lot this maximum stands on. */
    lotId: string;
    /**
     * `max_bids.placed_during_pre_bid` — TRUE iff the maximum was stored before
     * the auction opened. The sole membership test; see the file header.
     */
    placedDuringPreBid: boolean;
    /** The row's CURRENT lifecycle state (`active` / `superseded` / …). */
    status: string;
    /** ISO placement timestamp — the ordering key. */
    createdAt: string;
    /** Stable tiebreak when two rows share a timestamp to the millisecond. */
    maxBidId: string;
}

export interface SelectLotPreBidsOptions<T extends LotPreBidCandidate = LotPreBidCandidate> {
    /**
     * Narrow the auction-wide stream to one lot. Omitted = every lot's pre-bids,
     * which is what a cross-lot view wants.
     */
    lotId?: string;
    /**
     * Keep maximums the clerk has cancelled. Default `false` — a cancelled
     * pre-bid is a real delta the stream must deliver (that IS the delete), but
     * a list of standing maximums shouldn't render it. `true` for a history view.
     */
    includeCancelled?: boolean;
    /** Override the ordering. Defaults to newest-placed first — see below. */
    sortBy?: (a: T, b: T) => number;
}

/**
 * Newest-placed first.
 *
 * BL-1325 follow-up: this used to be oldest-first, on the stated grounds that it
 * was "the order a clerk reads a pre-bid list in" — with nothing behind that
 * claim, and no acceptance criterion specifying pre-bid order at all. It sat
 * directly beneath the live bid record, which sorts NEWEST first by `bids.seq`,
 * so one card reversed direction halfway down: the live half put its "High bid"
 * row at the top while the pre-bid half buried the standing top maximum at the
 * bottom. The older Timed Controls feed this section replaces was already
 * newest-first. One card now reads one direction.
 */
function byNewestPlacedFirst(a: LotPreBidCandidate, b: LotPreBidCandidate): number {
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
    // `created_at` is microsecond-precision in Postgres but parses to
    // millisecond precision here, so same-transaction rows can tie. `maxBidId`
    // is not chronological — it only guarantees a STABLE order, which is all
    // that is needed to stop rows swapping places between renders.
    return a.maxBidId < b.maxBidId ? 1 : -1;
}

/**
 * Select a lot's pre-bids out of the auction-wide stream, newest first.
 *
 * @param rows - every `max_bids` row the stream has delivered for the auction,
 *   pre-bid or not.
 * @param opts - {@link SelectLotPreBidsOptions}.
 * @returns the matching pre-bids, ordered newest-placed first. The input array
 *   is not mutated.
 */
export function selectLotPreBids<T extends LotPreBidCandidate>(
    rows: readonly T[],
    opts: SelectLotPreBidsOptions<T> = {},
): T[] {
    const { lotId, includeCancelled = false, sortBy } = opts;
    const selected = rows.filter((row) => {
        if (lotId !== undefined && row.lotId !== lotId) return false;
        // The membership test. Everything else here is scoping, not membership.
        if (!row.placedDuringPreBid) return false;
        if (!includeCancelled && row.status === 'cancelled') return false;
        return true;
    });
    selected.sort(sortBy ?? byNewestPlacedFirst);
    return selected;
}
